//#region ../src/utils/concurrency.ts
/**
* Concurrency Utilities
*
* Provides helpers for limiting the number of concurrent async operations.
* Used to prevent excessive memory consumption when scanning many files
* in parallel (e.g., reading zip metadata during scenario discovery).
*/
/**
* Map over an array with a concurrency limit on the async mapper function.
*
* Behaves like `Promise.all(items.map(fn))` but limits how many mapper
* invocations run simultaneously. Results are returned in the same order
* as the input items.
*
* @param items - Array of items to process
* @param limit - Maximum number of concurrent mapper invocations
* @param mapper - Async function to apply to each item
* @returns Array of results in the same order as input items
* @throws Re-throws the first error from any mapper invocation (fail-fast)
*/
async function mapWithConcurrencyLimit(items, limit, mapper) {
	if (limit < 1) throw new RangeError(`Concurrency limit must be >= 1, got ${limit}`);
	const results = new Array(items.length);
	if (items.length === 0) return results;
	let nextIndex = 0;
	const worker = async () => {
		while (nextIndex < items.length) {
			const currentIndex = nextIndex++;
			results[currentIndex] = await mapper(items[currentIndex], currentIndex);
		}
	};
	const workerCount = Math.min(limit, items.length);
	const workers = [];
	for (let i = 0; i < workerCount; i++) workers.push(worker());
	await Promise.all(workers);
	return results;
}
//#endregion
export { mapWithConcurrencyLimit };
